/**
 * The exercise stage — canvas, run lifecycle, full screen, instant stop.
 *
 * Extracted from the Phase 2 preview screen so there is ONE implementation of
 * the run loop rather than a copy per screen. What lives here is subtle and
 * clinically load-bearing: device-pixel canvas sizing, resize handling, the
 * full-screen-before-start ordering, and the spacebar stop. A second copy would
 * drift, and a fix would reach only one of them.
 *
 * It knows the exercise CONTRACT and no exercise (§1). It is handed a
 * definition and parameter values and asks the registry for the runner.
 *
 * Consumers:
 *   - the prescription builder's preview (Phase 3)
 *   - the exercise library at /clinician/preview, kept permanently
 *   - the patient session runner (Phase 4), which is exactly this plus
 *     recording, and later the safety monitor, both on the emit seam
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  getRunner,
  type ExerciseDefinition,
  type ExerciseInstance,
  type InputMode,
  type MetricSample,
  type ParamValues,
  type Position,
  type StopReason,
} from '@pathways/exercises';
import { scaleModelFactor } from './calibration.js';
import styles from './ExerciseStage.module.css';

/** Debounce before a parameter change restarts the run, in milliseconds. */
const RESTART_DEBOUNCE_MS = 180;

/**
 * How long to wait for full screen before starting anyway.
 *
 * `requestFullscreen()` normally settles in a frame or two — it resolves, or it
 * rejects because the browser refused. It can also do neither: a window that is
 * not compositing never completes the transition and the promise stays pending
 * forever. Awaiting that unconditionally means the exercise never starts, and
 * the failure is silent — the patient sees the stop button and a blank stage,
 * and would reasonably sit there believing the session is running.
 *
 * Full screen is an enhancement, not a precondition. If it arrives late the
 * ResizeObserver below resizes the canvas and the exercise re-resolves its
 * geometry, which is the same path a window resize already takes.
 */
const FULLSCREEN_TIMEOUT_MS = 1200;

export interface ExerciseStageHandle {
  /**
   * Begin. MUST be called from a user gesture — entering full screen requires
   * transient activation, and the browser will refuse it from a timer.
   */
  start(): Promise<void>;
  stop(reason: StopReason): void;
}

export interface ExerciseStageProps {
  definition: ExerciseDefinition;
  params: ParamValues;
  position: Position;
  inputMode: InputMode;
  pxPerDegree: number;
  /** Ask for full screen when the run starts. */
  fullScreen?: boolean;
  onSample?: (sample: MetricSample) => void;
  /**
   * The parameter values the exercise says it is ACTUALLY delivering, which a
   * screen too small for the prescription will shrink. Reported whenever the
   * geometry resolves, so the last call wins.
   */
  onDelivered?: (values: Record<string, number>) => void;
  onRunningChange?: (running: boolean) => void;
  onStopped?: (reason: StopReason) => void;
  /**
   * What it MEANS when the run ends because full screen was left — Escape, or
   * the browser's own control.
   *
   * On a clinician screen that is the doctor closing a preview. On the patient
   * runner the same keypress is a patient stopping their therapy, and recording
   * it as a clinician stop would put a stop the doctor never made into the
   * patient's record. Same event, two different facts.
   */
  exitStopReason?: StopReason;
  /** Shown over the canvas while idle. */
  idleHint?: string;
  /**
   * Rendered INSIDE the stage element, over the canvas.
   *
   * This is how the patient runner puts its stop control on the exercise screen
   * (§8: large, persistent, on every exercise screen). It has to be inside:
   * full screen renders the stage element's subtree and nothing else, so a
   * button anywhere else on the page is simply not on screen at the one moment
   * a symptomatic patient needs it.
   */
  children?: ReactNode;
}

export const ExerciseStage = forwardRef<ExerciseStageHandle, ExerciseStageProps>(
  function ExerciseStage(
    {
      definition,
      params,
      position,
      inputMode,
      pxPerDegree,
      fullScreen = true,
      onSample,
      onDelivered,
      onRunningChange,
      onStopped,
      exitStopReason = 'clinician_stop',
      idleHint = 'Press Run',
      children,
    },
    ref,
  ) {
    const [running, setRunning] = useState(false);
    const [prompt, setPrompt] = useState<string | null>(null);

    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const stageRef = useRef<HTMLDivElement | null>(null);
    const instanceRef = useRef<ExerciseInstance | null>(null);

    // Callbacks are read through a ref inside the run loop so that a parent
    // re-render does not tear down and restart a running exercise.
    const handlers = useRef({ onSample, onDelivered, onRunningChange, onStopped });
    handlers.current = { onSample, onDelivered, onRunningChange, onStopped };

    const setRunningState = useCallback((next: boolean) => {
      setRunning(next);
      handlers.current.onRunningChange?.(next);
    }, []);

    const stop = useCallback(
      (reason: StopReason) => {
        instanceRef.current?.stop(reason);
        setRunningState(false);
        handlers.current.onStopped?.(reason);
        if (document.fullscreenElement) void document.exitFullscreen();
      },
      [setRunningState],
    );

    /**
     * Enter full screen BEFORE starting, so the canvas is already at its final
     * size when the exercise resolves its geometry. Starting first and
     * expanding afterwards leaves the target sized for the small canvas.
     */
    const start = useCallback(async () => {
      if (fullScreen && stageRef.current && document.fullscreenEnabled) {
        try {
          await Promise.race([
            stageRef.current.requestFullscreen(),
            new Promise((resolve) => window.setTimeout(resolve, FULLSCREEN_TIMEOUT_MS)),
          ]);
        } catch {
          // Denied or unsupported — run windowed rather than not at all.
        }
      }
      setRunningState(true);
    }, [fullScreen, setRunningState]);

    useImperativeHandle(ref, () => ({ start, stop }), [start, stop]);

    // Leaving full screen — Escape, or the browser's own control — stops the
    // run. Otherwise the exercise carries on invisibly behind the page.
    useEffect(() => {
      const onChange = () => {
        if (!document.fullscreenElement && running && fullScreen) {
          instanceRef.current?.stop(exitStopReason);
          setRunningState(false);
          handlers.current.onStopped?.(exitStopReason);
        }
      };
      document.addEventListener('fullscreenchange', onChange);
      return () => document.removeEventListener('fullscreenchange', onChange);
    }, [running, fullScreen, exitStopReason, setRunningState]);

    // --- the run loop --------------------------------------------------------
    // Restarting on any parameter change is what makes the controls feel live.
    // The debounce keeps a held-down stepper from thrashing the exercise.
    useEffect(() => {
      if (!running) return;

      const observers: ResizeObserver[] = [];

      const timer = window.setTimeout(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        // The host sizes and clears the canvas — contract in types.ts.
        //
        // The backing store is sized in DEVICE pixels, not CSS pixels. On a
        // Windows machine at 125% or 150% display scaling those differ, and a
        // canvas sized in CSS pixels gets stretched by the compositor — which
        // is exactly what makes a moving target look soft.
        const sizeToDevicePixels = () => {
          const rect = canvas.getBoundingClientRect();
          const dpr = window.devicePixelRatio || 1;
          const width = Math.round(rect.width * dpr);
          const height = Math.round(rect.height * dpr);
          if (canvas.width !== width || canvas.height !== height) {
            canvas.width = width;
            canvas.height = height;
          }
        };
        sizeToDevicePixels();

        // Full screen changes the element's size after the fact; keep up.
        const observer = new ResizeObserver(sizeToDevicePixels);
        observer.observe(canvas);
        observers.push(observer);

        // A canvas smaller than the screen shows a SCALE MODEL of the
        // full-screen view, not a crop of it. Measured here, after
        // sizeToDevicePixels() and after any full-screen transition has
        // settled, so the factor describes the canvas the exercise will
        // actually resolve its geometry against. At full screen this is 1.
        const cssRect = canvas.getBoundingClientRect();
        const effectivePxPerDegree =
          pxPerDegree * scaleModelFactor(cssRect.width, cssRect.height);

        let finished = false;
        const instance = getRunner(definition.id)({
          params,
          position,
          inputMode,
          pxPerDegree: effectivePxPerDegree,
          canvas,
          now: () => performance.now(),
          emit: (sample) => handlers.current.onSample?.(sample),
          reportDelivered: (values) => handlers.current.onDelivered?.(values),
          finish: (reason) => {
            if (finished) return;
            finished = true;
            instanceRef.current?.stop(reason);
            setRunningState(false);
            handlers.current.onStopped?.(reason);
          },
          setPrompt,
        });

        instanceRef.current = instance;
        instance.start();
      }, RESTART_DEBOUNCE_MS);

      return () => {
        window.clearTimeout(timer);
        for (const observer of observers) observer.disconnect();
        instanceRef.current?.stop('clinician_stop');
        instanceRef.current = null;
        setPrompt(null);
      };
    }, [running, definition, params, position, inputMode, pxPerDegree, setRunningState]);

    // §8: spacebar stops instantly. The same key the patient will use, so the
    // doctor feels the actual stop latency rather than being told about it.
    useEffect(() => {
      if (!running) return;
      const onKeyDown = (event: KeyboardEvent) => {
        if (event.code !== 'Space') return;
        event.preventDefault();
        stop('patient_stop');
      };
      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
    }, [running, stop]);

    // A right-click must do nothing here. The browser's own menu — "Save image",
    // "Inspect" — would otherwise open over the target while the exercise keeps
    // running underneath. This is not a response: nothing is read from it.
    return (
      <div
        className={styles.stage}
        ref={stageRef}
        onContextMenu={(event) => event.preventDefault()}
      >
        <canvas ref={canvasRef} className={styles.canvas} data-exercise-canvas />
        {prompt && <div className={styles.prompt}>{prompt}</div>}
        {!running && <div className={styles.idle}>{idleHint}</div>}
        {children}
      </div>
    );
  },
);
